import { useEffect, useState } from "react";
import { View, StyleSheet, ActivityIndicator, Pressable, Linking } from "react-native";
import { useGlobalSearchParams } from "expo-router";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { supabase } from "@/lib/supabase-client";
import Avatar from "@/components/Avatar";

interface ContactData {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  phone_number?: string | null;
  email_address?: string | null;
}

export default function OnsiteContactDetailPage() {
  // useGlobalSearchParams (not Local): nested [shift] routes do not hand Local the parent param -> stuck loading
  const params = useGlobalSearchParams<{ shift: string }>();
  const shiftId = params.shift;
  const { colors } = useTheme();

  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState<ContactData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!shiftId) return;

    (async () => {
      setLoading(true);
      try {
        // Fetch shift to get onsite_contact id (from shift or project)
        const { data: shiftData, error: shiftError } = await supabase
          .from("shifts")
          .select("meta_data, project:projects(meta_data)")
          .eq("id", shiftId)
          .single();

        if (shiftError) throw shiftError;

        const contactId =
          (shiftData?.meta_data as any)?.onsite_contact || (shiftData?.project as any)?.meta_data?.onsite_contact;

        if (!contactId) {
          setError("No onsite contact assigned");
          return;
        }

        // Fetch contact details
        const { data: contactData, error: contactError } = await supabase
          .from("contacts")
          .select("id, first_name, last_name, phone_number, email_address")
          .eq("id", contactId)
          .single();

        if (contactError) throw contactError;
        setContact(contactData);
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : "Failed to load contact");
      } finally {
        setLoading(false);
      }
    })();
  }, [shiftId]);

  if (loading) {
    return (
      <View style={[styles.container, styles.center, { backgroundColor: colors.background }]}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  if (error || !contact) {
    return (
      <View style={[styles.container, styles.center, { backgroundColor: colors.background }]}>
        <MaterialCommunityIcons name="account-alert" size={48} color={colors.placeholder} />
        <ThemedText style={{ marginTop: 12, color: colors.textSecondary }}>{error || "Contact not found"}</ThemedText>
      </View>
    );
  }

  const displayName = `${contact.first_name || ""} ${contact.last_name || ""}`.trim() || "Unknown Contact";
  const phone = contact.phone_number;

  const handleCall = () => {
    if (phone) Linking.openURL(`tel:${phone}`);
  };

  const handleMessage = () => {
    if (phone) Linking.openURL(`sms:${phone}`);
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      {/* Profile Card */}
      <View style={[styles.profileCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
        <Avatar firstName={contact.first_name ?? undefined} lastName={contact.last_name ?? undefined} size={80} />
        <ThemedText style={styles.name}>{displayName}</ThemedText>
      </View>

      {/* Contact Actions */}
      {phone ? (
        <View style={[styles.actionsCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <ThemedText style={[styles.sectionTitle, { color: colors.textSecondary }]}>Contact</ThemedText>

          <View style={styles.actionRow}>
            <View style={styles.actionContent}>
              <ThemedText style={styles.actionTitle}>Phone</ThemedText>
              <ThemedText style={[styles.actionSubtitle, { color: colors.textSecondary }]}>{phone}</ThemedText>
            </View>
            <View style={styles.actionButtons}>
              <Pressable
                onPress={handleCall}
                style={({ pressed, hovered }) => [
                  styles.actionButton,
                  { backgroundColor: colors.emerald?.background, borderColor: colors.emerald?.border },
                  hovered && { opacity: 0.8 },
                  pressed && { opacity: 0.6 },
                ]}
              >
                <MaterialCommunityIcons name="phone" size={20} color={colors.emerald?.text} />
              </Pressable>
              <Pressable
                onPress={handleMessage}
                style={({ pressed, hovered }) => [
                  styles.actionButton,
                  { backgroundColor: colors.violet?.background, borderColor: colors.violet?.border },
                  hovered && { opacity: 0.8 },
                  pressed && { opacity: 0.6 },
                ]}
              >
                <MaterialCommunityIcons name="message-text" size={20} color={colors.violet?.text} />
              </Pressable>
            </View>
          </View>
        </View>
      ) : (
        <View style={[styles.actionsCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <View style={styles.noContact}>
            <MaterialCommunityIcons name="phone-off" size={32} color={colors.placeholder} />
            <ThemedText style={[styles.noContactText, { color: colors.textSecondary }]}>
              No phone number available
            </ThemedText>
          </View>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
  },
  center: {
    alignItems: "center",
    justifyContent: "center",
  },
  profileCard: {
    alignItems: "center",
    padding: 24,
    borderRadius: 12,
    borderWidth: StyleSheet.hairlineWidth,
    marginBottom: 16,
  },
  name: {
    fontSize: 22,
    fontWeight: "700",
    marginTop: 12,
    marginBottom: 4,
  },
  actionsCard: {
    borderRadius: 12,
    borderWidth: StyleSheet.hairlineWidth,
    overflow: "hidden",
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: "600",
    textTransform: "uppercase",
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 8,
  },
  actionRow: {
    flexDirection: "row",
    alignItems: "center",
    padding: 12,
    paddingHorizontal: 16,
    gap: 12,
  },
  actionContent: {
    flex: 1,
  },
  actionButtons: {
    flexDirection: "row",
    gap: 8,
  },
  actionButton: {
    width: 40,
    height: 40,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
  },
  actionTitle: {
    fontSize: 15,
    fontWeight: "500",
  },
  actionSubtitle: {
    fontSize: 12,
    marginTop: 1,
  },
  noContact: {
    alignItems: "center",
    padding: 24,
    gap: 8,
  },
  noContactText: {
    fontSize: 14,
  },
});
